import React, { useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { testimonials as fallbackTestimonials } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [testimonials, setTestimonials] = useState(fallbackTestimonials);
  const { t } = useLanguage();

  // Fetch testimonials from API on mount
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/public/testimonials`);
        if (response.data && response.data.length > 0) {
          setTestimonials(response.data);
        }
      } catch (error) {
        console.error('Error fetching testimonials from API, using fallback:', error);
      }
    };
    fetchTestimonials();
  }, []);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section id="reviews" className="py-24 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-amber-100 rounded-full blur-3xl opacity-50" />
      <div className="absolute bottom-20 right-10 w-64 h-64 bg-amber-100 rounded-full blur-3xl opacity-50" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-amber-700 font-medium tracking-widest text-sm uppercase">{t('testimonials.subtitle')}</span>
          <h2 className="text-4xl md:text-5xl font-serif text-slate-800 mt-3 mb-4">{t('testimonials.title')}</h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            {t('testimonials.description')}
          </p>
        </div>

        {/* Featured Testimonial */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-slate-50 to-amber-50/50 rounded-3xl p-8 md:p-12 shadow-xl relative">
            {/* Quote Icon */}
            <div className="absolute -top-6 left-12">
              <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center shadow-lg">
                <Quote className="w-6 h-6 text-white" />
              </div>
            </div>

            {/* Testimonial Content */}
            <div className="mt-6">
              {/* Stars */}
              <div className="flex gap-1 mb-6">
                {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                  <Star key={`star-${i}`} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>

              {/* Text */}
              <blockquote className="text-xl md:text-2xl text-slate-700 leading-relaxed mb-8 font-light italic">
                "{testimonials[currentIndex].text}"
              </blockquote>

              {/* Author */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center text-white text-xl font-semibold">
                    {testimonials[currentIndex].name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">{testimonials[currentIndex].name}</p>
                    <p className="text-slate-500 text-sm">{testimonials[currentIndex].location}</p>
                  </div>
                </div>
                <span className="text-slate-500 text-sm">{testimonials[currentIndex].date}</span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={prevTestimonial}
              aria-label="Previous review"
              className="w-12 h-12 rounded-full border-2 border-slate-200 flex items-center justify-center text-slate-600 hover:border-amber-500 hover:text-amber-600 transition-colors duration-300"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            {/* Dots */}
            <div className="flex gap-2">
              {testimonials.map((testimonial, index) => (
                <button
                  key={testimonial.id || testimonial.name + index}
                  type="button"
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`${t('testimonials.title')} ${index + 1} / ${testimonials.length}`}
                  aria-current={currentIndex === index}
                  className="p-1.5 flex items-center"
                >
                  <span className={`block h-3 rounded-full transition-all duration-300 ${currentIndex === index ? 'w-8 bg-amber-500' : 'w-3 bg-slate-300 hover:bg-slate-400'}`} />
                </button>
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              aria-label="Next review"
              className="w-12 h-12 rounded-full border-2 border-slate-200 flex items-center justify-center text-slate-600 hover:border-amber-500 hover:text-amber-600 transition-colors duration-300"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* All Reviews Summary */}
        <div className="grid grid-cols-2 gap-6 mt-16 max-w-xl mx-auto">
          <div className="text-center border-r border-slate-200">
            <span className="text-4xl font-bold text-amber-600">100+</span>
            <p className="text-slate-500 text-sm mt-1">{t('testimonials.reviews')}</p>
          </div>
          <div className="text-center">
            <span className="text-4xl font-bold text-amber-600">4★</span>
            <p className="text-slate-500 text-sm mt-1">{t('testimonials.hotelRating')}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
